import {
  SubscriptionError,
  createSubscription,
  hasWildcard,
  incrementUpdateCount,
  isActive,
  markAsActive,
  matchesPattern,
} from '~/composables/mercure';
import type {
  MercureUpdate,
  Subscription,
  SubscriptionId,
  UpdateHandler,
} from '~/composables/mercure';

/**
 * Handles active subscriptions and dispatches updates to the right handlers.
 * Uses an inverted index for fast exact lookups and efficient wildcard matching.
 */
export class SubscriptionManager {
  private readonly subscriptions: Map<SubscriptionId, Subscription>;
  private readonly exactTopicIndex: Map<string, Set<SubscriptionId>>;
  private readonly wildcardSubscriptions: Set<SubscriptionId>;

  constructor () {
    this.subscriptions = new Map();
    this.exactTopicIndex = new Map();
    this.wildcardSubscriptions = new Set();
  }

  subscribe<T = unknown>(
    topics: string[],
    handler: UpdateHandler<T>,
  ): Subscription<T> {
    if (!topics || topics.length === 0) {
      throw new SubscriptionError('At least one topic is required');
    }

    if (typeof handler !== 'function') {
      throw new SubscriptionError('Handler must be a function');
    }

    const subscription = createSubscription<T>({ topics, handler });

    this.subscriptions.set(subscription.id, subscription as Subscription);

    const activeSubscription = markAsActive(subscription);
    this.subscriptions.set(activeSubscription.id, activeSubscription as Subscription);

    this.buildIndexForSubscription(activeSubscription);

    return activeSubscription;
  }

  unsubscribe (id: SubscriptionId): void {
    const subscription = this.subscriptions.get(id);
    if (!subscription) {
      return;
    }

    this.removeFromIndex(subscription);
    this.subscriptions.delete(id);
  }

  getSubscription (id: SubscriptionId): Subscription | undefined {
    return this.subscriptions.get(id);
  }

  getAllSubscriptions (): Subscription[] {
    return Array.from(this.subscriptions.values());
  }

  async dispatch (update: MercureUpdate): Promise<number> {
    let notifiedCount = 0;
    const notifiedSubscriptions = new Set<SubscriptionId>();

    const topicStr = update.topic;
    const exactMatches = this.exactTopicIndex.get(topicStr);

    if (exactMatches) {
      for (const subId of exactMatches) {
        if (await this.notifySubscription(subId, update)) {
          notifiedCount++;
          notifiedSubscriptions.add(subId);
        }
      }
    }

    for (const subId of this.wildcardSubscriptions) {
      if (notifiedSubscriptions.has(subId)) {
        continue;
      }

      const subscription = this.subscriptions.get(subId);
      if (!subscription || !isActive(subscription)) {
        continue;
      }

      const matches = subscription.topics.some(pattern =>
        matchesPattern(pattern, update.topic),
      );

      if (matches) {
        if (await this.notifySubscription(subId, update)) {
          notifiedCount++;
        }
      }
    }

    return notifiedCount;
  }

  hasSubscriptions (): boolean {
    return this.subscriptions.size > 0;
  }

  getSubscriptionCount (): number {
    return this.subscriptions.size;
  }

  clear (): void {
    this.subscriptions.clear();
    this.exactTopicIndex.clear();
    this.wildcardSubscriptions.clear();
  }

  private buildIndexForSubscription (subscription: Subscription): void {
    let hasWildcardTopic = false;

    for (const topic of subscription.topics) {
      if (hasWildcard(topic)) {
        hasWildcardTopic = true;
      } else {
        if (!this.exactTopicIndex.has(topic)) {
          this.exactTopicIndex.set(topic, new Set());
        }
        const topicSubscriptions = this.exactTopicIndex.get(topic);
        if (topicSubscriptions) {
          topicSubscriptions.add(subscription.id);
        }
      }
    }

    if (hasWildcardTopic) {
      this.wildcardSubscriptions.add(subscription.id);
    }
  }

  private removeFromIndex (subscription: Subscription): void {
    for (const topic of subscription.topics) {
      if (!hasWildcard(topic)) {
        const subs = this.exactTopicIndex.get(topic);
        if (subs) {
          subs.delete(subscription.id);
          if (subs.size === 0) {
            this.exactTopicIndex.delete(topic);
          }
        }
      }
    }

    this.wildcardSubscriptions.delete(subscription.id);
  }

  private async notifySubscription (
    subscriptionId: SubscriptionId,
    update: MercureUpdate,
  ): Promise<boolean> {
    const subscription = this.subscriptions.get(subscriptionId);
    if (!subscription || !isActive(subscription)) {
      return false;
    }

    try {
      await subscription.handler(update);

      const updated = incrementUpdateCount(subscription);
      this.subscriptions.set(updated.id, updated);

      return true;
    } catch (error) {
      console.error(
        `[SubscriptionManager] Handler error for subscription ${subscription.id}:`,
        error,
      );
      return false;
    }
  }
}
